import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  ClipboardList,
  Cpu,
  Images,
  KeyRound,
  Lightbulb,
  ListChecks,
  LogOut,
  Users,
} from 'lucide-react';

import { Modal } from '../common/index.jsx';
import { ChangePasswordForm } from '../../pages/LoginPage.jsx';
import { useAuthStore } from '../../store/authStore.js';
import { useUiStore } from '../../store/uiStore.js';

const ANNOTATOR_NAV = [
  { to: '/', label: 'My queue', icon: ClipboardList, end: true },
  { to: '/guidelines', label: 'Guidelines', icon: BookOpen },
  { to: '/examples', label: 'Examples', icon: Lightbulb },
];

const ADMIN_NAV = [
  { to: '/admin', label: 'Progress', icon: BarChart3, end: true },
  { to: '/admin/samples', label: 'All captions', icon: Images },
  { to: '/admin/agent-outputs', label: 'Agent Outputs', icon: Cpu },
  { to: '/admin/assignments', label: 'Assignments', icon: ListChecks },
  { to: '/admin/users', label: 'People', icon: Users },
];

export function AppShell({ children }) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [changingPassword, setChangingPassword] = useState(false);
  const showTagLetters = useUiStore((s) => s.showTagLetters);
  const toggleTagLetters = useUiStore((s) => s.toggleTagLetters);
  const toast = useUiStore((s) => s.toast);
  const location = useLocation();

  // The annotation workspace is full-bleed: the sidebar would eat space the caption
  // needs, and the page has its own header.
  const focusMode = location.pathname.startsWith('/annotate/');

  if (focusMode) {
    return <div className="main">{children}</div>;
  }

  const isAdmin = user?.role === 'admin';
  const initials = (user?.display_name || user?.username || '?')
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="app-shell">
      <nav className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-name">Caption Annotation</div>
          <div className="brand-sub">Hallucination evaluation</div>
        </div>

        <div className="sidebar-nav">
          <div className="nav-group">
            <div className="nav-group-label">Annotate</div>
            {ANNOTATOR_NAV.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </div>

          {isAdmin && (
            <div className="nav-group">
              <div className="nav-group-label">Administration</div>
              {ADMIN_NAV.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </div>
          )}
        </div>

        <div className="sidebar-foot stack">
          <label className="row gap-1 tiny muted" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={showTagLetters} onChange={toggleTagLetters} />
            Letters on tags
          </label>

          <div className="row-between">
            <div className="user-badge">
              <span className="avatar">{initials}</span>
              <span className="who">
                <strong>{user?.display_name}</strong>
                <span>{user?.role}</span>
              </span>
            </div>
            <div className="row gap-1">
              {/* Anyone may change their own password, whether or not they were made to.
                  Before this the form existed only on the forced first-sign-in screen,
                  so waiving that requirement left an account unable to change it at all. */}
              <button
                type="button"
                className="btn ghost icon"
                onClick={() => setChangingPassword(true)}
                aria-label="Change password"
                title="Change your password"
              >
                <KeyRound size={15} />
              </button>
              <button type="button" className="btn ghost icon" onClick={logout} aria-label="Sign out">
                <LogOut size={15} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {changingPassword && (
        <Modal
          title="Change your password"
          description="You stay signed in here. Anywhere else you are signed in will be signed out."
          onClose={() => setChangingPassword(false)}
        >
          <ChangePasswordForm
            submitLabel="Change password"
            onDone={() => {
              toast('Password changed', { tone: 'ok' });
              setChangingPassword(false);
            }}
          />
        </Modal>
      )}

      <main className="main">{children}</main>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
      <Icon size={15} />
      {label}
    </NavLink>
  );
}
